import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { timeout } from './timeout';
import { loadLazyImage } from './image';
import { withTimeout } from './async';

export class SplineSceneController {
  isLoaded = false;
  isPlaying = false;
  prevTimestamp = null;
  minFrameTime = Math.floor(1000 / 60);
  framesRendered = 0;

  constructor(options = {}) {
    const {
      parent,
      canvas,
      poster = null,
      fallback = null,
      sceneURL,

      // ScrollTrigger
      start = 'top bottom',
      end = 'bottom+=100px top',

      // Visuals
      fadeDuration = 0.2,
      fadeDelayOnEnter = 0.25,

      // LocalStorage-based load postponement
      localStorageKey = 'splineLoaded', // base key; will be made unique per page
      useFirstLoadDelays = true,
      firstLoadDelayMs = 4500,
      repeatLoadDelayMs = 100,

      // Takes first frame screenshot for the poster
      takeScreenshot = false,

      // Spline runtime
      splineLoadTimeout = 5000,
      failOnMissingWebGL2 = true,

      // Optional hook
      onLoaded = null,
    } = options;

    this.parent = parent;
    this.canvas = canvas;
    this.poster = poster;
    this.fallback = fallback;
    this.sceneUrl = sceneURL;

    this.start = start;
    this.end = end;
    this.fadeDuration = fadeDuration;
    this.fadeDelayOnEnter = fadeDelayOnEnter;

    // Page-scoped localStorage key
    this.localStorageKeyBase = localStorageKey;
    this.localStorageKey = this._computePageScopedKey(localStorageKey);

    this.useFirstLoadDelays = useFirstLoadDelays;
    this.firstLoadDelayMs = firstLoadDelayMs;
    this.repeatLoadDelayMs = repeatLoadDelayMs;

    this.splineLoadTimeout = splineLoadTimeout;
    this.failOnMissingWebGL2 = failOnMissingWebGL2;

    this.takeScreenshot = takeScreenshot;
    this.onLoaded = onLoaded;

    this.spline = null;
    this.trigger = null;
  }

  _normalizePath(pathname) {
    if (!pathname) return '/';
    if (pathname.length > 1 && pathname.endsWith('/')) {
      return pathname.slice(0, -1);
    }
    return pathname;
  }

  _computePageScopedKey(baseKey) {
    try {
      const path = this._normalizePath(
        window.location && window.location.pathname,
      );
      // Use pathname to scope per page (query/hash ignored)
      return `${baseKey}::${encodeURIComponent(path || '/')}`;
    } catch {
      // Fallback if window is unavailable
      return baseKey;
    }
  }

  async init() {
    if (!this.parent || !this.canvas || !this.sceneUrl) return null;

    // LocalStorage-based postponement (page-scoped)
    if (this.useFirstLoadDelays) {
      const isFirstLoad = localStorage.getItem(this.localStorageKey) !== 'true';
      if (isFirstLoad) {
        await timeout(this.firstLoadDelayMs);
        localStorage.setItem(this.localStorageKey, 'true');
      } else {
        await timeout(this.repeatLoadDelayMs);
      }
    }

    if (this.takeScreenshot) {
      this.canvas.getContext('webgl2', {
        preserveDrawingBuffer: true,
      });
    }

    this.enableHeroBlobDrift();

    try {
      await this._loadScene();
    } catch (e) {
      console.warn(
        `Spline scene is not loaded, falling back to poster: ${e.message}`,
      );
      this.showFallback();
      return;
    }

    if (this.takeScreenshot) {
      if (typeof this.takeScreenshot === 'number') {
        setTimeout(() => this.showScreenshotImage(), this.takeScreenshot);
      } else {
        this.showScreenshotImage();
      }
    }

    this.trigger = ScrollTrigger.create({
      trigger: this.parent,
      start: this.start,
      end: this.end,
      onLeave: () => {
        this.pause();
      },
      onLeaveBack: () => {
        this.pause();
      },
      onEnter: () => {
        this.play();
      },
      onEnterBack: () => {
        this.play();
      },
      // Note: relaunching entire spline scene is costly
//      onLeave: () => {
//        gsap.to(this.canvas, { opacity: 0, duration: this.fadeDuration });
//        this.dispose();
//        this.canvas.removeAttribute('style');
//      },
//      onEnterBack: async () => {
//        await this._loadScene();
//        this.play();
//        gsap.to(this.canvas, {
//          opacity: 1,
//          duration: this.fadeDuration,
//          delay: this.fadeDelayOnEnter,
//        });
//      },
    });

    if (this.canvas && ScrollTrigger.isInViewport(this.canvas)) {
      this.play();
    }

    return this;
  }

  play = () => {
    this.startTimestamp = document.timeline.currentTime;
    this.prevTimestamp = this.startTimestamp;
    this.isPlaying = true;
    if (this.spline) {
      this.spline.play();
    }
    requestAnimationFrame(this._loop);
  };

  pause = () => {
    this.isPlaying = false;
    if (this.spline) {
      this.spline.stop();
    }
  };

  _loop = (ts) => {
    if (!this.spline || !this.isLoaded || !this.isPlaying) return;

    if ((ts - this.prevTimestamp) > this.minFrameTime) {
      this.prevTimestamp = ts;
      this.spline.requestRender();
      this._revealScene();
    }

    requestAnimationFrame(this._loop);
  };

  _revealScene = () => {
    if (this.framesRendered >= 0 && this.framesRendered < 5) {
      this.framesRendered += 1;
    } else {
      this.framesRendered = -1;
      this.poster && this.poster.remove();
      this.canvas.hidden = false;
      this.canvas.style.opacity = 1;
    }
  }

  async showFallback() {
    if (!this.fallback) return;

    const img = this.fallback.querySelector('img');
    if (!img) return;

    gsap.set(this.fallback, { opacity: 0 });
    this.fallback.hidden = false;

    await loadLazyImage(img, { eager: true });

    gsap.to(this.fallback, { opacity: 1, duration: 1 });
  }

  showScreenshotImage() {
    const img = new Image();
    img.src = this.canvas.toDataURL();
    document.body.appendChild(img);
  }

  enableHeroBlobDrift(options = {}) {
    const container = this.parent;

    // Ensure centered on init (explicitly set vars to 0)
    container.style.setProperty('--blob-offset-x', '0px');
    container.style.setProperty('--blob-offset-y', '0px');

    const computed = getComputedStyle(container);
    const cssRange = parseFloat(computed.getPropertyValue('--blob-range')) || 0;
    const maxShiftDefault =
      cssRange ||
      Math.round(
        Math.min(container.clientWidth, container.clientHeight) * 0.15,
      );
    const maxShift = options.maxShift ?? maxShiftDefault;

    let raf = null;
    let targetX = 0,
      targetY = 0;

    const setOffsets = () => {
      raf = null;
      container.style.setProperty('--blob-offset-x', `${targetX}px`);
      container.style.setProperty('--blob-offset-y', `${targetY}px`);
    };

    const schedule = () => {
      if (raf == null) raf = requestAnimationFrame(setOffsets);
    };

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const relX = (e.clientX - rect.left) / rect.width; // 0..1
      const relY = (e.clientY - rect.top) / rect.height; // 0..1

      // Normalize to -1..1 where 0 is center
      const nx = Math.max(-1, Math.min(1, (relX - 0.5) * 2));
      const ny = Math.max(-1, Math.min(1, (relY - 0.5) * 2));

      // Move opposite to pointer
      targetX = -nx * maxShift;
      targetY = -ny * maxShift;

      schedule();
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
      schedule();
    };

    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);

    return () => {
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
    };
  }

  async _loadScene() {
    // await this._loadSceneAA();
    await this._loadSceneWithPromise();
  }

  // Loads the scene with async/await approach
  async _loadSceneAA() {
    if (this.spline) return;
    this._preLoadSceneCheck();

    try {
      const { Application } = await withTimeout(
        import('@splinetool/runtime'),
        this.splineLoadTimeout,
        'Spline runtime import',
      );

      this.spline = new Application(this.canvas, { renderMode: 'manual' });
      this.canvas.hidden = true;
      await this.spline.load(this.sceneUrl);
      this.spline.requestRender();
      await timeout(200);
      if (this.poster) this.poster.remove();
      this.canvas.hidden = false;

      this.isLoaded = true;
      if (typeof this.onLoaded === 'function') this.onLoaded();
    } catch (err) {
      // Prevent a "stuck" instance from blocking retries if load/import timed out or failed.
      this.spline = null;
      throw err;
    }
  }

  // Loads scene with multi-stage promise
  _loadSceneWithPromise() {
    if (this.spline) return;
    this._preLoadSceneCheck();

    // Make a named reference to this
    const thisRef = this;

    // Promise-based scene loader
    return withTimeout(
      import('@splinetool/runtime'),
      this.splineLoadTimeout,
      'Spline runtime import',
    )
      .then((runtime) => {
        thisRef.canvas.hidden = true;
        thisRef.canvas.style.opacity = 0;
        // { renderMode: 'manual' } is another option if used with thisRef.spline.play()
        thisRef.spline = new runtime.Application(thisRef.canvas, { renderMode: 'manual' });
      })
      .then(() => {
        return thisRef.spline.load(thisRef.sceneUrl);
      })
      .then(() => {
        return thisRef.spline.requestRender();
      })
      .then(() => {
        thisRef.isLoaded = true;
        thisRef.play();
        if (typeof thisRef.onLoaded === 'function') {
          thisRef.onLoaded();
        }
        return timeout(250);
      })
      .then(() => {
        if (thisRef.poster) {
          return thisRef.poster.remove();
        } else {
          return;
        }
      })
      .then(() => {
        thisRef.canvas.hidden = false;
      })
      .catch((reason) => {
        // Prevent a "stuck" instance from blocking retries if load/import timed out or failed.
        thisRef.spline = null;
        console.error('Failed to load SplineScene');
        console.error(reason);
      });
  }

  _preLoadSceneCheck() {
    // Optional hard requirement for WebGL2
    if (this.failOnMissingWebGL2) {
      const testCanvas = this.canvas;
      const gl2 = testCanvas.getContext && testCanvas.getContext('webgl2');

      if (!gl2) {
        throw new Error('WebGL2 is required but not available.');
      }
    }
  }

  getSpline() {
    return this.spline;
  }

  async reload() {
    this.dispose();
    await this._loadScene();
  }

  dispose() {
    if (this.spline) {
      try {
        this.spline.dispose();
      } catch {}
      this.spline = null;
    }
  }

  destroy() {
    if (this.trigger) {
      this.trigger.kill();
      this.trigger = null;
    }
    this.dispose();
  }
}
